import React from "react";
import { Link } from "react-router-dom";
import { Calendar, Clock, Utensils, Tag, ChevronRight, Star } from "lucide-react";
import { retreatSlug, resolveBasePriceInr } from "../lib/retreatSlug";
import { getDisplayableBatches, getBatchPrices } from "../lib/batchDisplay";
import { getItemDetailPath } from "../lib/offeringRoutes";
import { formatScheduleCompact } from "../lib/scheduleDisplay";
import { getTeacherImage, isTeacherListingItem } from "../lib/teacherImages";
import { useCurrency } from "../context/CurrencyContext";
import { formatFromInr } from "../lib/currency";
import TeacherScheduleBlock from "./TeacherScheduleBlock";

function YogaCard({ yoga, hidePrices = false }) {
  const { currency, rates } = useCurrency();

  const formatSourceLabel = (value) => {
    if (value == null) return value;
    const raw = String(value).trim();
    if (!raw) return raw;

    return raw.charAt(0).toUpperCase() + raw.slice(1);
  };

  const getMealsLabel = (meals) => {
    const normalized = (meals || "").toString().trim();
    if (!normalized || normalized.toLowerCase() === "n/a") return "Not mentioned";

    const lower = normalized.toLowerCase();
    if (lower.includes("not mentioned") || lower.includes("not provided") || lower.includes("unknown")) {
      return "Not mentioned";
    }

    if (lower.includes("included") || lower.includes("provided")) {
      return normalized;
    }

    return normalized;
  };

  const formatInrAmount = (amountInr) => formatFromInr(amountInr, currency, rates);

  const getRetreatPrice = () => {
    const amountInr = resolveBasePriceInr(yoga, rates);
    return amountInr != null ? formatInrAmount(amountInr) : null;
  };

  const formatBatchPrice = (amountInr) => formatInrAmount(amountInr);
  const displayableBatches = getDisplayableBatches(yoga.batches, currency, formatBatchPrice);

  const isTeacherOffering = isTeacherListingItem(yoga);
  const isSchoolGroup = Boolean(yoga?.is_school_group);
  const isGroupedProvider = isTeacherOffering || isSchoolGroup;
  const teacherPhoto = isTeacherOffering
    ? getTeacherImage({
        instructor_slug: yoga.instructor_slug,
        teacher_id: yoga.instructor_id,
        profile_image: yoga.thumbnail_url,
      })
    : "";

  const getImageSrc = () => {
    if (yoga?.source === "realhappiness") {
      return "/images/realhappiness.jpg";
    }
    if (yoga?.source === "patanjaliyoga") {
      return "/images/patanjali_yoga_foundation.jpg";
    }
    if (isTeacherOffering) {
      return teacherPhoto;
    }
    if (yoga?.thumbnail_url) {
      const thumb = String(yoga.thumbnail_url).trim();
      if (/^https?:\/\//i.test(thumb) || /^data:image\//i.test(thumb)) {
        return thumb;
      }
      const raw = thumb.replace(/^\/+/, "");
      const path = raw.startsWith("images/") ? raw : `images/${raw}`;
      return `/${path}`;
    }
    return "/images/nalanda.jpg";
  };

  const detailPath = getItemDetailPath(yoga) || (yoga.retreat_key ? `/retreats/${retreatSlug(yoga)}` : null);
  const externalUrl = !detailPath && yoga.source_url ? yoga.source_url : null;
  const isLinkable = Boolean(detailPath || externalUrl);
  const instructorName = yoga.instructor_name || "";
  const cardTitle = isTeacherOffering
    ? instructorName || yoga.retreat_name
    : yoga.retreat_name;
  const cardSubtitle = isGroupedProvider
    ? yoga.card_subtitle ||
      (isTeacherOffering && yoga.retreat_name && yoga.retreat_name !== instructorName
        ? yoga.retreat_name
        : "")
    : "";
  const teacherScheduleLines =
    isGroupedProvider && Array.isArray(yoga.schedule_lines) && yoga.schedule_lines.length > 0
      ? yoga.schedule_lines
      : [];
  const teacherSchedule =
    isTeacherOffering && teacherScheduleLines.length === 0
      ? formatScheduleCompact(yoga.schedule_json, yoga.schedule_summary)
      : "";

  const cardClassName = [
    "h-full bg-white border border-gray-100 rounded-2xl overflow-hidden hover:shadow-2xl transition-all duration-300 flex flex-col group hover:-translate-y-1 text-left no-underline text-inherit",
    isLinkable ? "cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const cardBody = (
    <>
      <div className="relative h-56 overflow-hidden bg-gradient-to-br from-gray-50 to-gray-100">
        <img 
          src={getImageSrc()} 
          alt={cardTitle} 
          className={`h-full w-full transition-transform duration-700 group-hover:scale-110 ${
            isTeacherOffering ? "object-cover object-top" : "object-cover"
          }`}
          loading="lazy"
          decoding="async"
          width="448"
          height="224"
          onError={(e) => {
            const current = e.target.currentSrc || e.target.src || "";
            // Don't replace a working data/https upload with the placeholder on transient errors
            if (/^data:image\//i.test(current) || /^https?:\/\//i.test(current)) {
              return;
            }
            if (isTeacherOffering && teacherPhoto && e.target.src !== teacherPhoto) {
              e.target.src = teacherPhoto;
              return;
            }
            e.target.src =
              yoga?.source === "patanjaliyoga"
                ? "/images/patanjali_yoga_foundation.jpg"
                : "/images/nalanda.jpg";
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
        {/* Type badge stays off the image for grouped provider/teacher cards */}
        {!isGroupedProvider && (
          <div className="absolute top-4 left-4 flex flex-wrap gap-2">
            <span className="bg-white/95 backdrop-blur-sm text-gray-800 text-[10px] font-bold px-3 py-1.5 rounded-lg shadow-md uppercase tracking-wider border border-white/20">
              {yoga.type}
            </span>
          </div>
        )}
        {(() => {
          if (hidePrices) {
            return (
              <div className="absolute bottom-4 right-4 bg-emerald-600 text-white px-4 py-2 rounded-xl font-semibold shadow-lg text-sm">
                On request
              </div>
            );
          }
          const retreatPrice = getRetreatPrice();
          if (retreatPrice) {
            return (
              <div className="absolute bottom-4 right-4 bg-emerald-600 text-white px-4 py-2 rounded-xl font-semibold shadow-lg">
                {retreatPrice}
              </div>
            );
          }
          return (
            <div className="absolute bottom-4 right-4 bg-emerald-600 text-white px-4 py-2 rounded-xl font-semibold shadow-lg text-sm">
              Price on request
            </div>
          );
        })()}
      </div>

      <div className="p-6 flex-grow flex flex-col">
        {isGroupedProvider && (
          <div className="mb-3">
            <span className="inline-block bg-gray-100 text-gray-700 text-[10px] font-bold px-2.5 py-1 rounded-md uppercase tracking-wider">
              {yoga.type || "Online Course"}
            </span>
          </div>
        )}
        <div className="mb-3">
          <h3 className="text-xl font-bold text-gray-900 leading-tight mb-2 group-hover:text-blue-600 transition-colors line-clamp-2 min-h-[3.5rem]">
            {cardTitle}
          </h3>
          {cardSubtitle ? (
            <p className="text-sm text-emerald-700 font-medium mb-1 line-clamp-2">{cardSubtitle}</p>
          ) : null}
          {hidePrices ? (
            <p className="text-xs text-gray-500 mb-1.5">Price &amp; schedule on request</p>
          ) : null}
          <p className="text-sm font-medium text-gray-500 flex items-center gap-2">
            <Tag size={14} className="text-blue-500" />
            {yoga.location}
          </p>
        </div>

        <div className="flex flex-wrap gap-2 mb-4">
          {yoga.styles && yoga.styles.slice(0, 3).map((tag, index) => (
            <span key={index} className="bg-gradient-to-r from-blue-50 to-indigo-50 text-blue-700 text-xs font-semibold px-3 py-1 rounded-full border border-blue-100">
              {tag}
            </span>
          ))}
        </div>

        <div className={`grid gap-4 mb-4 text-sm ${isGroupedProvider ? "grid-cols-1" : "grid-cols-2"}`}>
          <div className="flex items-center gap-2 text-gray-600 bg-gray-50 px-3 py-2 rounded-lg">
            <Clock size={16} className="text-blue-500" />
            <span className="font-medium">{yoga.duration || "N/A"}</span>
          </div>
          {!isGroupedProvider && (
            <div className="flex items-center gap-2 text-gray-600 bg-gray-50 px-3 py-2 rounded-lg">
              <Utensils size={16} className="text-green-500" />
              <span className="font-medium">{getMealsLabel(yoga.meals)}</span>
            </div>
          )}
        </div>

        {isGroupedProvider && (teacherScheduleLines.length > 0 || teacherSchedule) && (
          <TeacherScheduleBlock
            lines={teacherScheduleLines}
            fallbackSchedule={teacherSchedule}
            cardTitle={cardTitle}
            className="mb-4"
            maxItems={2}
            compact
          />
        )}

        {!isTeacherOffering && yoga.google_rating && (
          <div className="mb-3">
            <div className="flex items-center justify-between bg-gray-50 px-3 py-2 rounded-lg border border-gray-200">
              <div className="flex items-center gap-2">
                <div className="flex items-center">
                  <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                  <span className="ml-1 text-sm font-semibold text-gray-900">
                    {yoga.google_rating}
                  </span>
                </div>
                {yoga.google_review_count && (
                  <span className="text-xs text-gray-500">
                    ({yoga.google_review_count})
                  </span>
                )}
              </div>
              <span
                className="text-xs text-gray-500 font-medium"
                title="This rating is based on Google reviews"
              >
                Google rating
              </span>
            </div>
          </div>
        )}

        {displayableBatches.length > 0 && (
          <div className="mt-auto pt-4 border-t border-gray-50">
            <h3 className="text-[11px] font-bold text-gray-400 uppercase tracking-widest mb-2 flex items-center gap-1">
              <Calendar size={12} /> Upcoming Batches
            </h3>
            <div className="space-y-1.5 max-h-24 overflow-y-auto pr-1 custom-scrollbar">
              {displayableBatches.slice(0, 3).map((batch, idx) => {
                const { sharedPrice: sharedDiscountPrice, privatePrice: privateDiscountPrice } = getBatchPrices(
                  batch,
                  currency,
                  formatBatchPrice
                );
                const courseDateLabel = (batch?.course_date || "").toString().trim();

                return (
                  <div key={idx} className="flex flex-col gap-1 text-xs p-1.5 rounded-lg bg-gray-50 hover:bg-blue-50 transition-colors">
                    <div className="flex justify-between items-center">
                      <span className="font-medium text-gray-700">{courseDateLabel}</span>
                      {batch.availability && !String(batch.availability).toLowerCase().includes("check website") && (
                        <span className={`text-[10px] font-bold ${batch.availability?.toLowerCase().includes("fully") ? "text-red-500" : "text-green-600"}`}>
                          {batch.availability}
                        </span>
                      )}
                    </div>
                    {!hidePrices && (
                      <div className="flex gap-3 text-[10px] text-gray-600">
                        {sharedDiscountPrice && (
                          <span>Shared: {sharedDiscountPrice}</span>
                        )}
                        {privateDiscountPrice && (
                          <span>Private: {privateDiscountPrice}</span>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="mt-auto flex items-center justify-between pt-4 border-t border-gray-50">
          <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
            {isTeacherOffering
              ? "Verified Teacher"
              : isSchoolGroup
                ? "Yoga School"
                : formatSourceLabel(yoga.source)}
          </span>
          {isLinkable ? (
            <span className="flex items-center gap-1 text-xs font-bold text-blue-600 group-hover:text-blue-800 transition-colors">
              Details <ChevronRight size={14} />
            </span>
          ) : null}
        </div>
      </div>
    </>
  );

  if (detailPath) {
    return (
      <Link
        to={detailPath}
        className={cardClassName}
        aria-label={`View details for ${cardTitle || "course"}`}
      >
        {cardBody}
      </Link>
    );
  }

  if (externalUrl) {
    return (
      <a
        href={externalUrl}
        target="_blank"
        rel="noreferrer"
        className={cardClassName}
        aria-label={`View details for ${cardTitle || "course"}`}
      >
        {cardBody}
      </a>
    );
  }

  return <div className={cardClassName}>{cardBody}</div>;
}

export default YogaCard;
